import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Compass, Home } from 'lucide-react';

/**
 * 404 page.
 *
 * The App.js catch-all route (`<Route path="*" />`) used to send any
 * typo'd URL back to `/` which rendered the SplashScreen — so a user
 * who tapped a stale link just saw the app's welcome animation and
 * had no idea they'd mistyped. This page tells them what happened
 * and gives them a way home.
 */
export default function NotFoundPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div className="p-4 animate-fadeIn min-h-screen flex items-center justify-center bg-farm-cream">
      <Card className="w-full max-w-md" data-testid="not-found-page">
        <CardContent className="flex flex-col items-center text-center py-12 px-6">
          <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-farm-primary/10">
            <Compass className="h-8 w-8 text-farm-primary" />
          </div>
          <h1 className="font-heading text-2xl font-bold mb-2 text-farm-forest">
            {t('notFound.title', { defaultValue: 'Page not found' })}
          </h1>
          <p className="text-muted-foreground mb-8">
            {t('notFound.body', {
              defaultValue: "The page you're looking for doesn't exist or has moved.",
            })}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <Button
              className="bg-farm-primary hover:bg-farm-primary-dark"
              onClick={() => navigate('/dashboard')}
              data-testid="not-found-home"
            >
              <Home className="w-4 h-4 mr-2" />
              {t('notFound.goHome', { defaultValue: 'Back to home' })}
            </Button>
            <Button variant="outline" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('notFound.goBack', { defaultValue: 'Go back' })}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
